import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { centsToEuros, formatPrice } from "@/lib/money.util";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { InfoAccordionRow, InfoRowNested } from "./infoCard";
import { formatDate } from "./expeditionDetail.utils";

type ExpeditionBillingDetail = NonNullable<
  GetOneExpeditionBoResponseDto["expeditionBillingDetail"]
>;

/** Invoice lines, in the order they are captured in the billing modals. Amounts are in cents. */
const BILLING_AMOUNT_KEYS = [
  "transportHt",
  "packaging",
  "packagingProtection",
  "taxes",
  "insurance",
  "tva",
] as const;

export const BillingAccordion = ({
  detail,
  isReturn = false,
}: {
  detail: ExpeditionBillingDetail;
  isReturn?: boolean;
}) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const invoiceData = detail.expeditionInvoiceData;
  const total = invoiceData
    ? BILLING_AMOUNT_KEYS.reduce((acc, key) => acc + invoiceData[key], 0)
    : null;
  const billedAt = formatDate(detail.billedDate);
  const amountLabels: Record<(typeof BILLING_AMOUNT_KEYS)[number], string> = {
    transportHt: t("expeditionDrawerBilling.transportHt"),
    packaging: t("expeditionDrawerBilling.packaging"),
    packagingProtection: t("expeditionDrawerBilling.packagingProtection"),
    taxes: t("expeditionDrawerBilling.taxes"),
    insurance: t("expeditionDrawerBilling.insurance"),
    tva: t("expeditionDrawerBilling.tva"),
  };

  return (
    <InfoAccordionRow
      label={
        isReturn ? t("expeditionDrawerDetails.returnBilling") : t("expeditionDrawerDetails.billing")
      }
      value={
        <span className="flex flex-col items-end gap-0.5">
          <span>{total === null ? "-" : formatPrice(centsToEuros(total))}</span>
          <span
            className={cn("text-xs font-normal", billedAt ? "text-gray-500" : "text-orange-500")}
          >
            {billedAt
              ? t("expeditionDrawerBilling.billedAt", { date: billedAt })
              : t("expeditionDrawerBilling.notBilled")}
          </span>
        </span>
      }
    >
      {detail.reference && (
        <InfoRowNested label={t("expeditionDrawerDetails.reference")} value={detail.reference} />
      )}
      {detail.billedSeparately && (
        <InfoRowNested
          label={t("expeditionDrawerBilling.billedSeparately")}
          value={t("common:boolean.yes")}
        />
      )}
      {invoiceData && (
        <>
          {BILLING_AMOUNT_KEYS.map((key) => (
            <InfoRowNested
              key={key}
              label={amountLabels[key]}
              value={formatPrice(centsToEuros(invoiceData[key]))}
            />
          ))}
          {total !== null && (
            <InfoRowNested
              label={t("expeditionDrawerBilling.totalTtc")}
              value={formatPrice(centsToEuros(total))}
            />
          )}
          {detail.transportDiscountPercentage !== 0 && (
            <InfoRowNested
              label={t("expeditionDrawerBilling.transportDiscount")}
              value={`${detail.transportDiscountPercentage}%`}
            />
          )}
          {detail.overallDiscountPercentage !== 0 && (
            <InfoRowNested
              label={t("expeditionDrawerBilling.overallDiscount")}
              value={`${detail.overallDiscountPercentage}%`}
            />
          )}
        </>
      )}
    </InfoAccordionRow>
  );
};
